/**
 * OperatorHome — the action-first finance home for the Operator persona.
 *
 * Operators want speed, not a briefing: a big "Create an invoice" affordance,
 * quick actions for the other common entries, and a glanceable read on today's
 * money. No two-tier disclosure — the whole screen is "what do you want to
 * make?".
 *
 * Reuses `briefings.today` for the greeting + the day's money figures; all
 * actions are plain links into the existing create flows.
 *
 * @see prototype/home/dashboard-simple.html — the Operator (Priya) variant
 */
import { Link } from "@tanstack/react-router";
import { FilePlus2, ReceiptText, HandCoins, ArrowRight } from "lucide-react";
import { Card, CardBody } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { paiseToInrShort } from "../../../lib/format.ts";

export function OperatorHome() {
  const todayQ = trpc.briefings.today.useQuery({});
  const today = todayQ.data;
  const firstName = (today?.greeting ?? "").replace(/^good morning,?\s*/i, "").trim();

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">
          Hi{firstName ? ` ${firstName}` : ""} — let&rsquo;s get to it.
        </h1>
        <p className="text-muted text-sm mt-1">
          Create what you need in a few taps. Most invoices take under a minute.
        </p>
      </header>

      {/* Primary action + quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4">
        <div className="relative overflow-hidden rounded-2xl bg-ink p-6 text-white shadow-md flex flex-col justify-between">
          <div>
            <h2 className="text-xl font-bold">Create an invoice</h2>
            <p className="mt-1 text-sm text-slate-300">
              Pick a customer, add items, done.
            </p>
          </div>
          <Link
            to="/sales/invoices/new"
            className="mt-5 inline-flex h-12 items-center justify-center gap-2.5 rounded-lg bg-sky-600 text-base font-bold text-white transition-colors hover:bg-sky-700"
          >
            <FilePlus2 size={18} /> New invoice
          </Link>
        </div>

        <div className="grid grid-rows-2 gap-4">
          <QuickAction
            to="/procurement/bills/new"
            icon={<ReceiptText size={17} />}
            title="Add a bill"
            sub="Record a purchase from a vendor"
          />
          <QuickAction
            to="/sales/collections"
            icon={<HandCoins size={17} />}
            title="Record a payment received"
            sub="Mark money you've collected"
          />
        </div>
      </div>

      {/* Today's money — glanceable, not a briefing */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat
          label="Sales this month"
          value={today ? paiseToInrShort(today.cash.balancePaise) : "—"}
          sub="received this month"
        />
        <Stat
          label="Money owed to you"
          value={today ? paiseToInrShort(today.receivables.totalPaise) : "—"}
          sub={
            today && today.receivables.overdueInvoiceCount > 0
              ? `${paiseToInrShort(today.receivables.overduePaise)} overdue`
              : "nothing overdue"
          }
        />
        <Stat
          label="Overdue invoices"
          value={today ? String(today.receivables.overdueInvoiceCount) : "—"}
          sub="need a reminder"
        />
      </div>

      <nav className="flex flex-wrap gap-2">
        <JumpLink to="/sales/invoices" label="All invoices" />
        <JumpLink to="/procurement/bills" label="Bills" />
        <JumpLink to="/sales/collections" label="Collections" />
        <JumpLink to="/sales/customers" label="Customers" />
      </nav>
    </div>
  );
}

function QuickAction(props: {
  to: "/procurement/bills/new" | "/sales/collections";
  icon: React.ReactNode;
  title: string;
  sub: string;
}) {
  return (
    <Link
      to={props.to}
      className="flex items-center gap-3 rounded-md border border-line bg-paper p-4 shadow-sm transition-colors hover:border-sky-300"
    >
      <span className="grid h-[34px] w-[34px] place-items-center rounded-[9px] bg-sky-50 text-sky-700 shrink-0">
        {props.icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{props.title}</span>
        <span className="block text-xs text-muted">{props.sub}</span>
      </span>
    </Link>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Card>
      <CardBody className="px-4 py-3.5">
        <div className="text-2xl font-bold tracking-tight tabular-nums">{value}</div>
        <div className="text-xs text-muted mt-0.5">{label}</div>
        <div className="text-xs text-muted">{sub}</div>
      </CardBody>
    </Card>
  );
}

function JumpLink(props: { to: "/sales/invoices" | "/procurement/bills" | "/sales/collections" | "/sales/customers"; label: string }) {
  return (
    <Link
      to={props.to}
      className="inline-flex items-center gap-1.5 rounded-md border border-line bg-paper px-3 py-1.5 text-sm font-medium text-ink-2 transition-colors hover:border-sky-300"
    >
      {props.label} <ArrowRight size={14} className="text-muted" />
    </Link>
  );
}
